import React, { useEffect, useState } from 'react';
import {
    Box, Button, Collapse, Grid, IconButton,
    Menu, MenuItem, TableBody, TableCell, TableHead, TableRow,
    Typography, lighten, useTheme
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DeleteConfirmationDialog, DialogTable, DialogTableTitle, SelectInput } from '@lp/lp-ui';
import RoleSelector from '../common/RoleSelector';
import { useAppSelector } from '../../app/hooks';
import { useCurrentUser } from '../../app/useCurrentUser';
import useCache from '../../app/useCache';
import { CachedEntity } from '../../app/cacheSlice';
import { OrganizationMemberModel } from '../../typings';
import { NewOrganizationMemberModel, toRoleModels } from '../../functions';
import { OrganizationMemberSchema } from '../../validationSchemes';
import { useFormik, FormikProvider, Form } from 'formik';

interface BulkImportOrganizationsPanelProps {
    organizations: OrganizationMemberModel[];
    onChange: (orgs: OrganizationMemberModel[]) => void;
}

const BulkImportOrganizationsPanel: React.FC<BulkImportOrganizationsPanelProps> = ({ organizations, onChange }) => {
    const theme = useTheme();
    const currentUser = useAppSelector(store => store.currentUser);
    const roles = useCache(CachedEntity.Role);
    const [isAdmin, isTenantAdmin] = useCurrentUser();

    const [editData, setEditData] = useState<OrganizationMemberModel>(NewOrganizationMemberModel());
    const [editOpen, setEditOpen] = useState(false);
    const [editOrgId, setEditOrgId] = useState<number>(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);

    // Non-tenant admins: auto-add their own organization
    useEffect(() => {
        if (!currentUser.data?.isTenantAdmin) {
            const existing = organizations.length > 0
                ? organizations[0]
                : {
                    organizationId: currentUser.data?.organization.id ?? 0,
                    organizationName: currentUser.data?.organization.name ?? "",
                    organizationDisplayName: currentUser.data?.organization.displayName ?? "",
                    roles: []
                };
            onChange([existing]);
            setEditData(existing);
            setEditOrgId(existing.organizationId);
            setEditOpen(true);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleEditClose = () => {
        setEditOpen(false);
        setAnchorEl(null);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, org: OrganizationMemberModel) => {
        setAnchorEl(event.currentTarget);
        setEditData(org);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDelete = () => {
        handleMenuClose();
        if (editData) {
            onChange(organizations.filter(o => o.organizationId !== editData.organizationId));
        }
        setEditData(NewOrganizationMemberModel());
        setDeleteDialogOpen(false);
    };

    const formik = useFormik<OrganizationMemberModel>({
        initialValues: editData,
        enableReinitialize: true,
        validationSchema: OrganizationMemberSchema,
        onSubmit: async (values) => {
            const index = organizations.findIndex(o => o.organizationId === editOrgId && editOrgId !== 0);
            if (index > -1) {
                const clone = [...organizations];
                clone[index] = values;
                onChange(clone);
            } else {
                onChange([...organizations, values]);
            }
            formik.resetForm();
            handleEditClose();
        }
    });

    const onOrganizationSelected = (event: React.ChangeEvent<any>) => {
        formik.handleChange(event);
        const organizationId = event.target.value;
        setEditOrgId(organizationId);
        const organization = currentUser.data?.organizations?.find(o => o.id === organizationId);
        formik.setFieldValue("organizationName", organization ? organization.name : '');
        formik.setFieldValue("organizationDisplayName", organization ? organization.displayName : '');
    };

    const handleRolesChange = (ids: number[]) => {
        const nextRoles = toRoleModels(ids, roles, formik.values.roles);
        formik.setFieldValue("roles", nextRoles);
        // For non-tenant admin reflect changes immediately
        if (!isTenantAdmin) {
            onChange([{ ...formik.values, roles: nextRoles }]);
        }
    };

    const isNew = editData.organizationId === 0;

    return (
        <React.Fragment>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Select organizations and roles to assign to all imported users.
            </Typography>

            <Box mx={-5}>
                <DialogTableTitle title="Organizations">
                    <React.Fragment>
                        {!editOpen && isAdmin &&
                            <IconButton size="large"
                                onClick={() => { setEditData(NewOrganizationMemberModel()); setEditOrgId(0); setEditOpen(true); }}
                            >
                                <AddCircleOutlineIcon />
                            </IconButton>
                        }
                    </React.Fragment>
                </DialogTableTitle>

                <Collapse in={editOpen} sx={{ border: '1px solid lightgrey' }}>
                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={formik.submitForm}>
                            <Box py={3} px={5} sx={{ background: lighten(theme.palette.primary.light, 0.5) }}>
                                <Grid container spacing={0} columnSpacing={6}>
                                    <Grid item xs={12}>
                                        <SelectInput
                                            label="Organization"
                                            isEditing={isNew && isAdmin}
                                            required
                                            {...formik.getFieldProps("organizationId")}
                                            options={[
                                                { value: 0, label: '-' },
                                                ...(currentUser.data?.organizations
                                                    ? currentUser.data.organizations
                                                        .filter(o => !organizations.some(uo => uo.organizationId === o.id) || o.id === editOrgId)
                                                        .map(o => ({ value: o.id, label: o.displayName }))
                                                    : [])
                                            ]}
                                            onChange={onOrganizationSelected}
                                            error={Boolean(formik.touched.organizationId && formik.errors.organizationId)}
                                            helperText={formik.touched.organizationId && formik.errors.organizationId}
                                            value={formik.values.organizationId}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <RoleSelector
                                            roles={roles}
                                            selectedIds={formik.values.roles.map(r => r.id)}
                                            onChange={handleRolesChange}
                                            disabled={!isAdmin}
                                            height={380}
                                        />
                                    </Grid>
                                    {isTenantAdmin &&
                                        <Grid item xs={12}>
                                            <Box display="flex">
                                                <Box flexGrow={1} />
                                                <Button onClick={() => { formik.resetForm(); handleEditClose(); }}>Cancel</Button>
                                                <Button onClick={formik.submitForm}>Ok</Button>
                                            </Box>
                                        </Grid>
                                    }
                                </Grid>
                            </Box>
                        </Form>
                    </FormikProvider>
                </Collapse>

                <Collapse in={!editOpen}>
                    {organizations.length > 0 &&
                        <DialogTable>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Organization</TableCell>
                                    <TableCell>Organization Roles</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organizations.map((row, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{row.organizationDisplayName}</TableCell>
                                        <TableCell>{row.roles.map(r => r.name).join(", ")}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                aria-label="actions"
                                                size="small"
                                                color="primary"
                                                onClick={(e) => handleMenuOpen(e, row)}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </DialogTable>
                    }
                </Collapse>
            </Box>

            <DeleteConfirmationDialog
                title="Are you sure you'd like to remove this Organization?"
                open={deleteDialogOpen}
                handleClose={() => { setDeleteDialogOpen(false); setAnchorEl(null); }}
                handleSave={handleDelete}
            />

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => { setEditOrgId(editData.organizationId); setEditOpen(true); handleMenuClose(); }}>Edit</MenuItem>
                <MenuItem onClick={() => setDeleteDialogOpen(true)}>Delete</MenuItem>
            </Menu>
        </React.Fragment>
    );
};

export default BulkImportOrganizationsPanel;
